import React from "react";
import { useTheme } from "react-jss";
import { Dictionary } from "lodash";
import { kebabCase } from "../../../utils/lodash";
import useStyles from "./ChoiceListItemTextHighlighterRenderer.styles";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { VariableModel } from "../../../models/VariableModel";
import { QuestionType, isChoiceList } from "../../../models/enums/QuestionType";
import { ChoiceRef } from "../../choices/BaseChoice";
import useQuestion from "../../../hooks/useQuestion";
import ChoiceLabelRenderer from "./ChoiceLabelRenderer";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingBoolean, getSettingString } from "../../../models/utilities/settingUtilities";
import { isNullOrEmpty, removeHtmlTags } from "../../../utils";
import { ButtonChoiceTextHighlighter } from "../../shared/ButtonChoiceTextHighlighter";
import { translate } from "../../../models/utilities/translationUtilities";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { MentionModel } from "../../../models/MentionModel";

interface Props extends RendererProps {
    selected?: boolean;
    variable: VariableModel;
    isFirst?: boolean;
    isLast?: boolean;
    name?: string;
    gridRendering?: boolean;
    openEnd?: string;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    choice?: ChoiceModel;
    horizontal?: boolean;
    controlType?: any;
    disabled?: boolean;
    highlightedWordsByCategories: Dictionary<any[]>;
    highlightedWords: Dictionary<any[]>;
    selectedChoiceModels: ChoiceModel[];
    duplicateWords: string[];
    openEndValue?: { [key: string]: string };
    mentions: { [variableName: string]: MentionModel[] };
    toggleIsCategorySelected(isSelected: boolean, enable: boolean): void;
    onDeleteHighlight(shouldSave: boolean): void;
}
export default function ChoiceListItemTextHighlighterRenderer(props: Props) {
    const {
        choice,
        controlType,
        isFirst,
        isLast,
        disabled,
        openEnd,
        gridRendering,
        selected,
        variable,
        horizontal,
        highlightedWordsByCategories,
        selectedChoiceModels,
        highlightedWords,
        duplicateWords,
        language,
        openEndValue,
        mentions,
        toggleIsCategorySelected,
        onDeleteHighlight,
    } = props;
    const choiceRef: ChoiceRef = React.useRef();
    const question = useQuestion();
    const theme = useTheme();
    const classes = useStyles({ theme, selected, horizontal });
    const choiceLabel = removeHtmlTags(useTranslatable(choice, "TEXT"));
    if (!choice.value) {
        return (
            <div id={`${choice.elementId}_INPUT`} className={classes.choiceHeader}>
                <ChoiceLabelRenderer choice={choice} gridRendering={gridRendering} />
            </div>
        );
    }

    const type = isChoiceList(question.type) ? question.type : QuestionType.RadioButton;

    function onSelect() {
        onDeleteHighlight(false);
        // Counter for selection
        Object.entries(selectedChoiceModels).forEach(([id, selectedChoiceModel]) => {
            if (choiceRef.current) {
                const isOpenEndRequired = getSettingBoolean(question, "MakeCommentsMandatory");
                if (isOpenEndRequired && isNullOrEmpty(openEndValue[selectedChoiceModel.value])) {
                    toggleIsCategorySelected(true, true);
                } else {
                    toggleIsCategorySelected(true, false);
                }
            }

            Object.entries(highlightedWordsByCategories).forEach(([key, value]) => {
                if (mentions[key]) {
                    const mentionIndex = mentions[key].findIndex(
                        (item) => selectedChoiceModel.value === item.value,
                    );
                    if (mentionIndex > -1) {
                        mentions[key].splice(mentionIndex, 1);
                    }
                }
                const choiceIndex = value.findIndex(
                    (item) => selectedChoiceModel === item.choiceModel,
                );
                if (choiceIndex > -1) {
                    highlightedWordsByCategories[key].splice(choiceIndex, 1);
                }
            });
            let highlightedWord: string;
            const choiceText = translate(selectedChoiceModel, "TEXT", language);
            if (duplicateWords.includes(choiceText)) {
                highlightedWord = `${choiceText} `;
            } else {
                highlightedWord = choiceText;
            }

            highlightedWordsByCategories[choice.name].push({
                highlightedWord,
                choiceModel: selectedChoiceModel,
                // addd counter for selection to remember
            });
            Object.entries(highlightedWords).forEach(([key, value]) => {
                value.splice(0);
            });
        });
    }

    function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.key === " ") {
            onSelect();
            event.preventDefault();
        }
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            const choices = variable.choiceList;
            const index = choices.indexOf(choice) + (event.key === "ArrowDown" ? 1 : -1);
            if (choices[index]) {
                const siblingElement = document.getElementById(`${choices[index].elementId}_INPUT`);
                if (siblingElement) {
                    siblingElement.focus();
                    if (type === QuestionType.RadioButton) siblingElement.click();
                    event.preventDefault();
                }
            }
        }
    }

    const openEndSize = getSettingString(choice, "OpenEndSize", "Medium");

    return (
        <>
            <ButtonChoiceTextHighlighter
                id={`${choice.elementId}_INPUT`}
                type="radio"
                label={choiceLabel}
                onKeyDown={onKeyDown}
                onSelect={onSelect}
                hasOpenEnd={false}
                openEndSize={openEndSize}
                selected={selected}
                className={kebabCase(QuestionType[type])}
                horizontal={horizontal}
                renderChoice={() => (
                    <ChoiceContainer
                        {...getRendererProps(props)}
                        choiceRef={choiceRef}
                        choice={choice}
                        controlType={controlType}
                        openEnd={openEnd}
                        isFirst={isFirst}
                        isLast={isLast}
                        isDisabled={disabled}
                        isSelected={selected}
                        variable={variable}
                    />
                )}
            />
        </>
    );
}
